/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { AiType, GetAllGenerativeThreadsParams, PageResultThread, Thread, ThreadRating } from '@squiz/dxp-ai-client';
import { PaginationPageLimit, ThreadRatingParameter } from '@squiz/dxp-ai-types';
import { PERMISSIONS } from '@squiz/dxp-auth';
import { formatDate } from '@squiz/dxp-console-support';
import {
  API_TABLE_EVENT_PAGE_NEXT,
  API_TABLE_EVENT_PAGE_PREVIOUS,
  ApiTable,
  ApiTableFetchOptions,
  ApiTablePageTokens,
  ApiTableProps,
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  DatetimePicker,
  DatetimePickerResult,
  EmptyState,
  EmptyStateProps,
  ICON_HISTORY,
  ICON_LOCAL_OFFER,
  SdsIcon,
  SelectField,
  SelectFieldOption,
  TableCell,
  TableContent,
  TextField,
} from '@squiz/sds';
import { SETTING_LIST_DATA_IDS } from 'apps/console-core/src/Components/SettingList/SettingList.constants';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { from, lastValueFrom } from 'rxjs';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { getAiService } from '../../Services/ai';
import { isGenerative } from '../../Utilities/formPredicates';
import { AI_THREAD_TABLE_CONSTANTS, AI_THREAD_TABLE_FILTER_DATA_IDS } from './AiThreadTable.constants';
import { AiThreadTableProps } from './AiThreadTable.props';
import { ThreadNameId } from './utils/ThreadNameId';
import { ThreadRatingWithIcon } from './utils/ThreadRatingWithIcon';
import { ThreadTagChips } from './utils/ThreadTagChips';

/**
 * Component to list both conversation and generative data types
 */
export const AiThreadTable = (props: AiThreadTableProps): JSX.Element => {
  const { heading, description, aiId, type } = props;

  const { t } = useTranslation();
  const apiTableRef = useRef(null);
  const labels = useMemo(() => {
    return {
      allRatings: t(AI_THREAD_TABLE_CONSTANTS.allRatings) as string,
      apiError: t(AI_STRINGS.apiError) as string,
      apply: t(AI_STRINGS.apply) as string,
      date: t(AI_STRINGS.date) as string,
      feedback: t(AI_STRINGS.feedback) as string,
      filterByTags: t(AI_STRINGS.filterByTags) as string,
      id: t(AI_STRINGS.nameSlashId) as string,
      negative: t(AI_THREAD_TABLE_CONSTANTS.negative) as string,
      positive: t(AI_THREAD_TABLE_CONSTANTS.positive) as string,
      rating: t(AI_STRINGS.rating) as string,
      responses: t(AI_STRINGS.responses) as string,
      tags: t(AI_STRINGS.tags) as string,
      threadId: t(AI_STRINGS.threadId) as string,
      threadTitle: t(AI_STRINGS.threadTitle) as string,
      unrated: t(AI_STRINGS.unrated) as string,
      userAction: t(AI_STRINGS.userAction) as string,
    };
  }, [t]);

  const aiService = getAiService();

  const [cacheKey] = useState<string>('');
  const [emptyText, setEmptyText] = useState<string>(props.emptyText);
  const [pageResult, setPageResult] = useState<PageResultThread>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const tagsRef = useRef<HTMLInputElement>(null);
  const [dateTimeFilter, setDateTimeFilter] = useState<DatetimePickerResult>();
  const ratingFilterRef = useRef<HTMLInputElement>(null);
  const [selectedRatingFilter, setSelectedRatingFilter] = useState<ThreadRatingParameter | undefined>(undefined);

  const tableColumns: Array<TableCell> = isGenerative(props.type)
    ? [
        { content: labels.id, key: `id` },
        { content: labels.feedback, key: `rating` },
        { content: labels.userAction, key: `tags` },
        { content: labels.date, key: `date` },
      ]
    : [
        { content: labels.id, key: `id` },
        { content: labels.rating, key: `rating` },
        { content: labels.tags, key: `tags` },
        { content: labels.date, key: `date` },
      ];

  const tableRows = useMemo(() => {
    if (pageResult === undefined || pageResult.items === undefined) {
      return [];
    }
    const threads = pageResult.items;
    return threads.map((thread: Thread) => {
      const { timeLastInteracted, rating, tags } = thread;
      const date = timeLastInteracted ? formatDate(timeLastInteracted, true) : '-';
      return {
        date: date,
        id: <ThreadNameId aiType={type} thread={thread}></ThreadNameId>,
        rating: <ThreadRatingWithIcon rating={rating as ThreadRating}></ThreadRatingWithIcon>,
        tags: <ThreadTagChips tags={tags}></ThreadTagChips>,
      };
    });
  }, [pageResult, type]);

  // Page token object for pagination
  const pageTokens = useMemo(() => {
    const pageTokens: ApiTablePageTokens = {};

    if (pageResult && pageResult.links) {
      pageTokens.pageAfter = pageResult.links.nextPage;
      pageTokens.pageBefore = pageResult.links.prevPage;
    }

    return pageTokens;
  }, [pageResult]);

  const getThreads = useCallback(
    (options: ApiTableFetchOptions) => {
      const { eventType, pageSize } = options;
      setIsLoading(true);
      let cursor: number | undefined;
      if (eventType === API_TABLE_EVENT_PAGE_NEXT && pageTokens.pageAfter) {
        cursor = parseInt(pageTokens.pageAfter);
      }
      if (eventType === API_TABLE_EVENT_PAGE_PREVIOUS && pageTokens.pageBefore) {
        cursor = parseInt(pageTokens.pageBefore);
      }
      let filterByCSVTagString = undefined;
      if (tagsRef.current && tagsRef.current.value !== undefined && tagsRef.current.value !== '') {
        filterByCSVTagString = tagsRef.current.value;
      }
      /* istanbul ignore next */
      const dto: GetAllGenerativeThreadsParams = {
        aiId: aiId,
        cursor: cursor,
        filterByCSVTagString: filterByCSVTagString,
        filterByDateFrom: dateTimeFilter ? new Date(dateTimeFilter.startDateTime).toISOString() : undefined,
        filterByDateTo: dateTimeFilter ? new Date(dateTimeFilter.endDateTime).toISOString() : undefined,
        filterByRating: selectedRatingFilter,
        pageSize: pageSize as PaginationPageLimit,
        sortDirection: 'desc',
      };

      const getAllThreads = isGenerative(props.type)
        ? aiService.apiClient.generative.getAllGenerativeThreads
        : aiService.apiClient.chatbot.getAllChatBotThreads;

      lastValueFrom(from(getAllThreads(dto)))
        .then((response) => {
          setPageResult(response);
          setIsLoading(false);
          return true;
        })
        .catch((e) => {
          setPageResult({ items: [] });
          setIsLoading(false);
          setEmptyText(labels.apiError);
        });
    },
    [
      aiId,
      aiService.apiClient.chatbot.getAllChatBotThreads,
      aiService.apiClient.generative.getAllGenerativeThreads,
      dateTimeFilter,
      labels.apiError,
      pageTokens.pageAfter,
      pageTokens.pageBefore,
      props.type,
      selectedRatingFilter,
    ],
  );

  const tableContent: TableContent = { columns: tableColumns, rows: tableRows };
  const tableProps: ApiTableProps = {
    cacheKey: cacheKey,
    emptyText: emptyText,
    fetchData: getThreads,
    isSlim: true,
    loading: isLoading,
    pageTokens: pageTokens,
    perPageDefault: 20,
    perPageOptions: [PaginationPageLimit.small, PaginationPageLimit.medium, PaginationPageLimit.large],
    tableContent: tableContent,
  };

  /* istanbul ignore next */
  const handleDatetime = useCallback((result: DatetimePickerResult) => {
    setDateTimeFilter(result);
  }, []);

  const handleRatingChange = useCallback((option: SelectFieldOption) => {
    const newValue = option.value;
    if (newValue === 'all') {
      setSelectedRatingFilter(undefined);
    } else {
      setSelectedRatingFilter(newValue as ThreadRatingParameter | undefined);
    }
  }, []);

  const ratingSelectFieldOptions: Array<SelectFieldOption> = [
    {
      dataId: AI_THREAD_TABLE_FILTER_DATA_IDS.all,
      key: AI_THREAD_TABLE_FILTER_DATA_IDS.all,
      label: labels.allRatings,
      value: AI_THREAD_TABLE_FILTER_DATA_IDS.all,
    },
    {
      dataId: AI_THREAD_TABLE_FILTER_DATA_IDS.positive,
      key: AI_THREAD_TABLE_FILTER_DATA_IDS.positive,
      label: labels.positive,
      value: AI_THREAD_TABLE_FILTER_DATA_IDS.positive,
    },
    {
      dataId: AI_THREAD_TABLE_FILTER_DATA_IDS.negative,
      key: AI_THREAD_TABLE_FILTER_DATA_IDS.negative,
      label: labels.negative,
      value: AI_THREAD_TABLE_FILTER_DATA_IDS.negative,
    },
    {
      dataId: AI_THREAD_TABLE_FILTER_DATA_IDS.unrated,
      key: AI_THREAD_TABLE_FILTER_DATA_IDS.unrated,
      label: labels.unrated,
      value: AI_THREAD_TABLE_FILTER_DATA_IDS.unrated,
    },
  ];

  const threadTableFilter = (
    <div className="content-tool-table-filters-form">
      <DatetimePicker
        callback={handleDatetime}
        classes="content-tool-table-filters-date-picker"
        data-testid={AI_THREAD_TABLE_FILTER_DATA_IDS.dateTime}
        placeholder={AI_THREAD_TABLE_CONSTANTS.datetimePickerPlaceHolder}
      ></DatetimePicker>
      <SelectField
        callback={handleRatingChange}
        classes="content-tool-table-filters-select-field"
        data-testid={AI_THREAD_TABLE_FILTER_DATA_IDS.ratingSelect}
        defaultOption={ratingSelectFieldOptions[0]}
        fieldLabel={''}
        hideLabel
        id={AI_THREAD_TABLE_CONSTANTS.ratingFieldName}
        name={AI_THREAD_TABLE_CONSTANTS.ratingFieldName}
        options={ratingSelectFieldOptions}
        ref={ratingFilterRef}
        value={selectedRatingFilter || ''}
      ></SelectField>
      <TextField
        classes="content-tool-table-filters-tags"
        fieldLabel={AI_THREAD_TABLE_FILTER_DATA_IDS.tags}
        hideLabel
        icon={ICON_LOCAL_OFFER}
        id={AI_THREAD_TABLE_FILTER_DATA_IDS.tags}
        name={AI_THREAD_TABLE_FILTER_DATA_IDS.tags}
        placeholder={labels.filterByTags}
        ref={tagsRef}
      ></TextField>
      <Button
        format={BUTTON_FORMAT_SOFT}
        onClick={() =>
          getThreads({
            eventType: 'loading',
            pageIndex: 0,
            pageSize: 20,
          })
        }
        text={labels.apply}
        theme={BUTTON_THEME_DEFAULT}
      ></Button>
    </div>
  );

  const tablePermissions: Record<AiType, string> = {
    chatbot: PERMISSIONS.dxpAi.CHATBOT_THREAD_READ,
    generative: PERMISSIONS.dxpAi.GENERATIVE_THREAD_READ,
  };
  const hasReadPermissionForTable = (type: AiType): boolean => {
    return hasPermission(tablePermissions[type]);
  };

  if (!hasReadPermissionForTable(props.type)) {
    return <></>;
  }

  const emptyStateProps: EmptyStateProps = {
    caption: emptyText,
    dataId: SETTING_LIST_DATA_IDS.emptyState,
    isLite: true,
  };

  return (
    <div className="content-tool-table-container">
      <div className="content-tool-table-container__header">
        <div className="header-text">
          <SdsIcon svg={ICON_HISTORY}></SdsIcon>
          <span>{heading}</span>
        </div>
        <p className="content-tool-table-container__table-description">{description}</p>
      </div>
      <div className="ai_thread_table">
        {threadTableFilter}
        {pageResult && pageResult.items.length === 0 ? (
          <div className="service-settings__wrapper">
            <EmptyState {...emptyStateProps} />
          </div>
        ) : (
          <>
            <ApiTable {...tableProps} ref={apiTableRef} />
          </>
        )}
      </div>
    </div>
  );
};
